import { Base } from "./Base";
import { IJob, IDateRange, 
         ERequestStatus, EParagraphType } from "./shared/types";
import { User } from "./User";
import { Request } from "./Request";
import { Paragraph } from "./Paragraph";

import i18n, { daysBeforeToday } from "@/common/filters";
import { createDate, sortObjectArray } from "@/common/utils";

export class Job extends Base implements IJob {
  HalloId: string;
  Name: string;
  DateBegin: Date;
  DateEnd: Date;
  DateRange: IDateRange; //used for picking dates
  DateCompleted: Date;
  Assignee: User[];
  Results: Paragraph[];

  Request?: Request;

  ListIndex?: number = 0; //used in ListView for display
  
  protected _propertyNames = [
    'HalloId', 'Name', 'DateBegin', 'DateEnd', 'DateCompleted', 'Assignee', 'Results'
  ];

  constructor(job?:IJob) {
    super();
    if (job) {
      this.Properties = job;
    }
  }
  
  set Properties(job:IJob) {
    for (let p in job) {
      if (p=='Assignee') {
        this[p] = [];
        job[p].forEach((a) => {
          this[p].push(new User(a));
        });
      } else if (p=='Results') {
        this[p] = [];
        job[p].forEach((r) => {
          let result:Paragraph = new Paragraph();
          result.Job = this;
          result.Request = this.Request;
          this[p].push(result);
        });
      } else if (this._propertyNames.indexOf(p) != -1) {
        this[p] = job[p];
      }
    }
  }

  set PropertiesFromStorage(job:any) {
    if (job.id && job.id!=this.HalloId) {
      this.HalloId = job.id;
    }
    if (job.name && job.name!=this.Name) {
      this.Name = job.name;
    }
    let p;
    let mapping:any;
    job.dates = job.dates || {};
    if (job.begin_ts && job.end_ts) {
      job.dates.begin_ts = job.begin_ts;
      job.dates.end_ts = job.end_ts;
    }
    if (job.done_ts) {
      job.dates.done_ts = job.done_ts;
    }
    mapping = {
      begin_ts: 'DateBegin',
      end_ts: 'DateEnd',
      done_ts: 'DateCompleted'
    };
    for (p in mapping) {
      if (job.dates[p]) {
        this[mapping[p]] = createDate(job.dates[p]);
      }
    }
    if (job.assignee) {
      this.Assignee = [];
      job.assignee.forEach((a) => {
        let u = new User();
        u.PropertiesFromStorage = a;
        this.Assignee.push(u);
      });
    }
    if (job.results) {
      this.ResultsFromStorage = job.results;
    }
  }

  set ResultsFromStorage(results) {
    try {
      this.Results = [];
      results.forEach((r) => {
        let result = new Paragraph();
        result.PropertiesFromStorage = r;
        result.Request = this.Request;
        result.Job = this;
        this.Results.push(result);
      });
    } catch (error) {
      throw error;
    }
  }

  getAssigneeIndex(assigneeId:string) : number {
    let index = -1;
    if (this.Assignee && this.Assignee.length>0) {
      for (let i=0; i<this.Assignee.length; i++) {
        if (this.Assignee[i].HalloId==assigneeId) {
          index = i;
          break;
        }
      }
    }
    return index;
  }

  getResultIndex(resultId:string) : number {
    let index = -1;
    if (this.Results && this.Results.length>0) {
      for (let i=0; i<this.Results.length; i++) {
        if (this.Results[i].HalloId==resultId) {
          index = i;
          break;
        }
      }
    }
    return index;
  }

  get StatusColor() : string {
    //yellow-orange-assertive-green
    if (!(this.DateBegin && this.DateEnd)) return '';
    if (this.DateCompleted) return 'bg-green';
    let n, today = new Date();
    if (today <= this.DateBegin) {
      n = daysBeforeToday(this.DateBegin);
      return (n==-1) ? 'bg-yellow' : '';
    } else if (today <= this.DateEnd) {
      n = daysBeforeToday(this.DateEnd);
      return (n==-1) ? 'bg-assertive' : 'bg-orange';
    }
    return 'bg-assertive';
  }

  get HasBeenRescheduled() : boolean {
    //when DateBegin/DateEnd has been changed
    return false;
  }

  get CanChange() : boolean {
    if (!this.HalloId || !this.DateEnd) {
      return true;
    } else {
      let now = new Date();
      let hrsToDateEnd = Math.ceil((this.DateEnd.getTime() - now.getTime()) / 3600000);
      return (this.HalloId && hrsToDateEnd>=8 && this.Request.Status!=ERequestStatus.Closed);
    }
  }

  get HasResult() : boolean {
    if (!(this.Results && this.Results.length>0)) return false;
    let r:Paragraph;
    for (let i=0; i<this.Results.length; i++) {
      r = this.Results[i];
      if (r.Text && r.Type!=EParagraphType.Cancelled) {
        return true;
      }
    }
    return false;
  }

  get HasBeenCancelled() : boolean {
    if (!(this.Results && this.Results.length>0)) return false;
    let rs = this.Results.map((r) => {
      return r;
    });
    sortObjectArray(rs,'Dates.Updated desc');
    return !!rs[0].Text.match(/^cancelled$/i);
  }

  get Properties() : IJob {
    return <IJob>this.getProperties();
  }

  get AssigneeHead() : User {
    return (this.Assignee && this.Assignee.length>0) ? this.Assignee[0] : null;
  }

  get AssignmentText() : string {
    if (!(this.Name && this.DateBegin && this.DateEnd) || 
          this.Request.Status==ERequestStatus.Refused) {
      return '';
    }
    let lang = this.DeviceLanguage;
    let details = '';
    let comma = (lang=='en' ? ' ' : '');
    if (this.DateCompleted) {
      details += i18n.L('completedAt') + comma + i18n.DT(this.DateCompleted);
    } else {
      details += i18n.L('toBeImplementedDuring') + comma + i18n.DT(this.DateBegin);
      comma = (lang=='zh' ? '' : ' ');
      details += comma + i18n.L('timeTo') + comma + i18n.DT(this.DateEnd);
    }
    if (this.Assignee) {
      details += (lang=='zh' ? '，' : ' ');
      details += i18n.L('implementedBy') + (lang=='zh' ? '' : ' ');
      comma = lang=='zh' ? '、' : ', ';
      let last = this.Assignee.length - 1;
      this.Assignee.forEach((a,i) => {
        if (i>0) {
          details += (i==last) ? (' ' + i18n.L('and') + ' ') : comma;
        }
        details += a.FullName;
      });
    }
    details += lang=='zh' ? '。' : '.';
    return details;
  }

  get CompletionText() : string {
    if (!this.DateCompleted || this.Request.Status==ERequestStatus.Refused) {
      return '';
    }

    let details = i18n.L('implementedDuring') + ': ' + 
        i18n.D(this.DateBegin,'dd/MMM/yyyy') +  ' - ' +
        i18n.D(this.DateEnd,'dd/MMM/yyyy');

    let comma = ' ', fullStop = ' ';
    if (this.DeviceLanguage != 'th') {
      comma = ', ',
      fullStop = '; ';
    }
    details += fullStop;
    details += i18n.L('implementedBy') + ': ';
    this.Assignee.forEach((a,i) => {
      if (i>0) {
        details += (i==(this.Assignee.length - 1)) ? (' ' + i18n.L('and') + ' ') : comma;
      }
      details += a.FullName;
    });
    details += fullStop;
    
    details += i18n.L('jobCompletedOn') + ': ';
    details += i18n.D(this.DateCompleted,'dd/MMM/yyyy hh:mm');
    
    return details;
  }
}